/**
 * WHERE THE REPORT BUILDER LIVES. M10 / S10-01, extended by S10-02.
 *
 * **ONE PLACE, BECAUSE TWO PLACES ALREADY DRIFTED ONCE.** Sol's Capture links
 * were built in two components and drifted, and one of them turned out not to
 * navigate at all — on a phone, months later. A path written twice is a path
 * that can disagree with itself.
 *
 * **THE BUILDER HAS TWO REAL ADDRESSES AS OF S10-02**, one per step, so
 * Android's Back button walks between them as ordinary history. They are
 * expressed here so the Continue action, the route table and the tests cannot
 * fall out of step.
 *
 * **A REPORT BELONGS TO ONE PROJECT** (PT §17), which is why the Project id is
 * a required argument rather than something read from the address bar. The id
 * in the resulting path grants nothing: every read behind it is filtered by
 * forced row-level security, and these are string builders with no authority.
 */

/** Step one — choose the Captures the report covers. */
export function reportBuilderPath(projectId: string): string {
  return `/projects/${projectId}/report`;
}

/** Step two — choose the photos and write the one optional Description. */
export function reportPhotosPath(projectId: string): string {
  return `${reportBuilderPath(projectId)}/photos`;
}

/** Step three — the client-facing preview. */
export function reportPreviewPath(projectId: string): string {
  return `${reportBuilderPath(projectId)}/preview`;
}

/**
 * The `state` key the full-screen viewer occupies in history — M10 / S10-02.
 *
 * **THE VIEWER IS A HISTORY ENTRY, NOT A VARIABLE, AND THAT IS THE WHOLE
 * POINT.** Opening a photo pushes an entry; Android's Back consumes it and the
 * photo closes, leaving Brandon exactly where he was. Without it, Back while
 * inspecting a photo would walk back an entire builder step — which is the
 * defect the S10-01 review found one level up, reappearing one level down.
 */
export const REPORT_VIEWER_STATE_KEY = 'reportViewerPhotoKey';

export interface ReportViewerLocationState {
  readonly [REPORT_VIEWER_STATE_KEY]?: string;
}

/** The photo the viewer is showing, or null when it is closed. */
export function viewerPhotoKey(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null;
  const key = (state as ReportViewerLocationState)[REPORT_VIEWER_STATE_KEY];
  return typeof key === 'string' && key.length > 0 ? key : null;
}
